<!--TODOS: Needs validation box? -->
<template>
  <!--FIXME: Missing labels for the menubar-->
  <div
    v-if="isDesktop || isEditMode"
    class="owc-header-main-navigation__desktop-wrapper"
    @keydown="handleKeyDownEvent"
  >
    <ValidationBoxWrapper v-if="isEditMode">
      <slot slot="validation-box" name="validation-box" />
    </ValidationBoxWrapper>
    <template v-if="isEditMode">
      <SlotContainer
        class="owc-header-main-navigation owc-header-main-navigation__desktop"
        elementDOM="ul"
        refName="slot-header-navigation-topic"
        :slot-component-data="JSON.stringify({ isTopicPartOfDesktopNavigation: true })"
      >
        <slot></slot>
      </SlotContainer>
    </template>
    <template v-else>
      <ul class="owc-header-main-navigation owc-header-main-navigation__desktop" role="menu" label="Menubar">
        <OwcHeaderNavigationTopic
          v-for="(data, index) in componentData.items"
          :key="index"
          :datajson="getDataForFlyoutListItem(data, index)"
        >
          <slot slot="seamless-vmos-flyout" name="seamless-vmos-flyout"></slot>
          <slot slot="fss-search-input" name="fss-search-input"></slot>
        </OwcHeaderNavigationTopic>
      </ul>
    </template>
  </div>
  <div v-else class="owc-header-main-navigation" aria-haspopup="true" :aria-expanded="shouldShowFlyout">
    <HeaderFlyout
      v-show="shouldShowFlyout"
      :languageConfigs="languageSwitch"
      :isThisFlyoutVisible="shouldShowFlyout"
      :imprint="imprint"
      :currentFlyoutLevel="flyoutLevel"
      :isFlyoutNavVisible="!isModelsFlyout"
      :isFirstLevelDesktop="isDesktop"
      :flyoutId="componentId"
      :flyoutInnerItems="componentData.items"
      :isAuthorEditMode="isEditMode"
      @closeFlyout="closeFlyout"
      @keydown="handleKeyDownEvent"
    >
      <slot slot="seamless-vmos-flyout" name="seamless-vmos-flyout"></slot>
      <slot slot="fss-search-input" name="fss-search-input"></slot>
    </HeaderFlyout>
  </div>
</template>

<script lang="ts">
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import HeaderFlyout from '@owc-packages/Header/src/HeaderFlyout.vue';
import OwcHeaderNavigationTopic from '@owc-packages/Header/src/HeaderNavigationTopic.vue';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';

import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import { Breakpoints } from '@owc-utils/MediaQueryService';
import { unlockWindowScroll, lockWindowScroll } from '@owc-utils/common/LockWindow';
import { AccessibilityFocusManager } from '@owc-utils/common/AccessibilityFocusHelper';
import getAccessibilityStackInstance from '@owc-utils/common/AccessibilityFocusStackHelper';
import headerMixin from '@owc-utils/Mixins/Header';
import OwcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import { HeaderMainNavigationComponent } from '@owc-packages/Header/src/HeaderMainNavigation';
import { HeaderEvents } from '@owc-utils/Seamless/headerEvents.enum';
import { FlyoutInnerCloseEvent } from '@owc-utils/Seamless/flyoutIntegrations.d';
import { HEADER_MAIN_NAV_FLYOUT, FOCUS_STACK_HEADER_CONTEXT } from '@owc-packages/Header/src/Header.configs';

import { buildHeaderComponentId } from './HeaderUtils';
import { HeaderNavigationTopic } from './HeaderNavigationTopic';

const accessibilityHelper = new AccessibilityFocusManager();
const globalFocusStack = getAccessibilityStackInstance();
const exclusivelyDesktopBreakpoints = [Breakpoints.mq5, Breakpoints.mq6];

export default (ComponentInitializeDispatcher as HeaderMainNavigationComponent).extend({
  name: 'owc-header-main-navigation',
  mixins: [headerMixin, OwcDataComponent],
  components: {
    HeaderFlyout,
    SlotContainer,
    OwcHeaderNavigationTopic,
    ValidationBoxWrapper,
  },
  props: {
    currentMq: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      shouldShowFlyout: false,
      isModelsFlyout: false,
      isLabelsSet: false,
    };
  },
  mounted() {
    this.onHeaderStateChange(state => {
      this.shouldShowFlyout = Boolean(this.isThisFlyoutOpen(state.flyoutChain, HEADER_MAIN_NAV_FLYOUT));
      this.isModelsFlyout = state.modelsFlyoutOpen;

      const isFlyoutOpen = this.shouldShowFlyout || this.isModelsFlyout;

      if (isFlyoutOpen) {
        lockWindowScroll();
      } else {
        unlockWindowScroll();
      }
    });

    if (this.componentData && this.componentData.items) {
      const firstTopic = this.componentData.items[0] as HeaderNavigationTopic;

      const id = buildHeaderComponentId('topic', firstTopic?.label.trim(), 0);
      this.setModelTopicId(id);
    }

    this.$el.addEventListener(
      HeaderEvents.MAIN_FLYOUT_CLOSE_EVENT,
      ({ detail: { isClose } }: CustomEvent<FlyoutInnerCloseEvent>) => this.closeFlyout(isClose),
    );
  },
  computed: {
    isDesktop() {
      return exclusivelyDesktopBreakpoints.includes(this.currentMq);
    },
    languageSwitch() {
      return this.componentData?.languageSwitch;
    },
    imprint() {
      return this.componentData?.imprint;
    },
    flyoutLevel() {
      return this.isDesktop ? 2 : 1;
    },
  },
  methods: {
    closeFlyout(shouldCloseAll: boolean) {
      this.closeHeaderFlyout(shouldCloseAll);
    },
    shouldOpenFlyoutOnFocus() {
      const shouldFlyoutBeOpen = this.shouldShowFlyout || this.isModelsFlyout;

      if (shouldFlyoutBeOpen) {
        const focusedElement = accessibilityHelper.tryToOpenFocusedElement(true);

        if (focusedElement) {
          globalFocusStack.pushToFocusStack(focusedElement, FOCUS_STACK_HEADER_CONTEXT);
        }
      }
    },
    accessibleFlyoutClick(keyboardEvent: KeyboardEvent) {
      const focusedElement = accessibilityHelper.tryToOpenFocusedElement(true);

      if (focusedElement) {
        globalFocusStack.pushToFocusStack(focusedElement, FOCUS_STACK_HEADER_CONTEXT);
        keyboardEvent.preventDefault();
      }
    },
    handleKeyDownEvent(keyboardEvent: KeyboardEvent) {
      if (!accessibilityHelper.isInitialized) {
        accessibilityHelper.init(this.$el as HTMLElement, '.owc-header-navigation-topic');
        accessibilityHelper.focusOnFirstElement();
      }

      switch (keyboardEvent.code) {
        case 'ArrowLeft':
          accessibilityHelper.focusOnPreviousElement();
          this.shouldOpenFlyoutOnFocus();
          keyboardEvent.preventDefault();
          break;
        case 'ArrowRight':
          accessibilityHelper.focusOnNextElement();
          this.shouldOpenFlyoutOnFocus();
          keyboardEvent.preventDefault();
          break;
        case 'ArrowDown':
        case 'Space':
        case 'Enter':
          this.accessibleFlyoutClick(keyboardEvent);
          keyboardEvent.preventDefault();
          break;
        case 'Home':
          accessibilityHelper.focusOnFirstElement();
          keyboardEvent.preventDefault();
          break;
        case 'End':
          accessibilityHelper.focusOnLastElement();
          keyboardEvent.preventDefault();
          break;
        case 'Tab':
          /*
           * FIXME: Chrome his breaking the tab flow order
           * when the tabindex attribute is set to -1 on the previously
           * focused element. This was working before the latest version of
           * chrome and it still works on other browsers.
           * This hopefully can be removed in time. If the chrome version is above
           * 97 this may already be working again without the settimeout
           * */
          setTimeout(() => accessibilityHelper.reset(), 400);
          globalFocusStack.resetWithContext(FOCUS_STACK_HEADER_CONTEXT);
          break;
      }
    },
    getDataForFlyoutListItem(data, index) {
      return JSON.stringify(Object.assign({ isTopicPartOfDesktopNavigation: true, listIndex: index }, data));
    },
  },
  watch: {
    componentData() {
      if (!this.isLabelsSet && this.componentData?.menuButtonAria) {
        this.setNavigationLabels({
          mobileMenuButton: this.componentData?.menuButtonAria,
          backButton: this.componentData?.backButtonAria,
          closeButton: this.componentData?.closeButtonAria,
        });

        this.isLabelsSet = true;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@owc-styles/utils';

.owc-header-main-navigation {
  @include breakpoint-from(mq3) {
    @include wb-content-width;

    position: relative;
  }

  &__desktop {
    display: block;

    &,
    & > .webcomponent.webcomponent-simulator,
    slot {
      display: flex;
      flex-direction: row;

      align-items: center;

      min-height: 56px;
    }
  }
}
</style>
